import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { API_CONFIG } from '../config/apiConfig';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';

const ProductCard = ({ product }) => {
  const cart = useCart();
  const wishlist = useWishlist();
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [variations, setVariations] = useState([]);
  
  // Robust price calculation
  // Handles cases where price fields are missing, null, string, or zero
  const parsePrice = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const str = String(value).trim();
    if (str === '') return 0;
    const cleaned = str.replace(/[^0-9.-]+/g, '');
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  const discountValue = parsePrice(product.discount);
  const hasDiscount = discountValue > 0;
  const originalPrice = parsePrice(product.original_price);
  // If sell_price is missing or zero, fallback to originalPrice
  const sellPriceRaw = parsePrice(product.sell_price);
  const sellPrice = sellPriceRaw > 0 ? sellPriceRaw : originalPrice;
  // Calculate discount percentage for badge
  const discountPercent = hasDiscount ? Math.round(discountValue) : 0;
  
  // Image handling: cover = main image, images = array of additional images
  // Parse images array from API response (it's a JSON string or array)
  let parsedImages = [];
  try {
    if (product.images && typeof product.images === 'string') {
      parsedImages = JSON.parse(product.images).filter(img => img && img.trim() !== '');
    } else if (Array.isArray(product.images)) {
      parsedImages = product.images.filter(img => img && img.trim() !== '');
    }
  } catch (e) {
    console.warn('Failed to parse product images:', e);
  }
  
  // Build full image array: [cover, ...images]
  const allImages = [];
  
  // Add cover as first image if available
  if (product.cover && product.cover.trim()) {
    const coverUrl = product.cover.startsWith('http') 
      ? product.cover 
      : `${API_CONFIG.IMAGE_URL}${product.cover}`;
    allImages.push(coverUrl);
  }
  
  // Add additional images
  parsedImages.forEach(img => {
    const imgUrl = img.startsWith('http') ? img : `${API_CONFIG.IMAGE_URL}${img}`;
    allImages.push(imgUrl);
  });
  
  // Fallback to default if no images
  const images = allImages.length > 0 ? allImages : ['/assets/img/product/default.png'];
  
  const [currentImage, setCurrentImage] = useState(0);
  const intervalRef = useRef(null);

  // Parse variations
  React.useEffect(() => {
    let parsedVariations = [];
    try {
      // Check if product has variations based on size field (Pure Meat convention)
      if ((product.size === '1' || product.size === 1) && product.variations && product.variations !== '[]' && product.variations !== '') {
        const parsed = typeof product.variations === 'string' 
          ? JSON.parse(product.variations) 
          : product.variations;
        
        if (Array.isArray(parsed) && parsed.length > 0) {
          parsedVariations = parsed.map(group => ({
            ...group,
            name: group.title || group.name,
            items: group.items.map(item => ({
              ...item,
              name: item.title || item.name,
              id: item.title || item.name
            }))
          }));
        }
      }
    } catch (err) {
      console.warn('Failed to parse variations:', err);
    }
    setVariations(parsedVariations);
  }, [product.variations, product.name, product.size]);

  // Check if product has variations (must match CartContext logic)
  const hasVariations = (product.size === '1' || product.size === 1) && 
                        variations.length > 0 && 
                        variations[0]?.items?.length > 0;

  // Check if product is in cart and get cart item
  const isInCart = cart.isInCart(product.id);
  const cartItem = isInCart ? cart.cart.find(item => item.id === product.id) : null;

  const updateQuantity = (change) => {
    if (cartItem) {
      const newQuantity = cartItem.quantiy + change;
      if (newQuantity <= 0) {
        cart.updateQuantity(product.id, 0); // This will remove the item
      } else {
        cart.updateQuantity(product.id, newQuantity);
      }
    }
  };

  const startImageCarousel = () => {
    if (images.length <= 1) return;
    let idx = 0;
    intervalRef.current = setInterval(() => {
      idx = (idx + 1) % images.length;
      setCurrentImage(idx);
    }, 700); // Change image every 700ms
  };

  const stopImageCarousel = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setCurrentImage(0);
  };

  return (
    <>
      <div className="product__items" style={{ width: '250px', margin: '0 auto' }}>
        <style>
          {`
            .product__items--content {
              display: flex;
              flex-direction: column;
              align-items: center;
              position: relative;
              padding-top: 12px;
            }
            .product__items--content__title {
              position: static !important;
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
              overflow: hidden;
              white-space: normal;
              word-break: break-word;
              line-height: 1.35;
              min-height: 2.7em;
              margin-bottom: 6px;
              opacity: 1 !important;
              visibility: visible !important;
              top: auto !important;
              left: auto !important;
              right: auto !important;
            }
            .product__items--price {
              margin-top: 4px;
              position: relative;
              z-index: 1;
            }
            .add__to--cart__btn {
              display: none;
              align-items: center;
              justify-content: center;
              width: 100%;
              margin: 6px auto 8px;
            }
            .product__items:hover .add__to--cart__btn {
              display: inline-flex;
            }
            .product__items--action {
              display: none;
              position: absolute;
              top: 10px;
              right: 10px;
              z-index: 10;
            }
            .product__items:hover .product__items--action {
              display: block;
            }
            .variant-modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: rgba(0, 0, 0, 0.7);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 99999;
            }
            .variant-modal-content {
              background: white;
              padding: 30px;
              border-radius: 10px;
              max-width: 500px;
              width: 90%;
              max-height: 80vh;
              overflow-y: auto;
              box-shadow: 0 4px 20px rgba(0,0,0,0.3);
            }
            .variant-modal-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 20px;
              padding-bottom: 15px;
              border-bottom: 2px solid #eee;
            }
            .variant-modal-close {
              background: none;
              border: none;
              font-size: 28px;
              cursor: pointer;
              color: #666;
              padding: 0;
              width: 30px;
              height: 30px;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .variant-modal-close:hover {
              color: #000;
            }
            .variant-option {
              display: block;
              padding: 15px;
              margin: 10px 0;
              border: 2px solid #ddd;
              border-radius: 8px;
              cursor: pointer;
              transition: all 0.3s;
            }
            .variant-option:hover {
              border-color: #007bff;
              background-color: #f8f9fa;
            }
            .variant-option.selected {
              border-color: #007bff;
              background-color: #e7f3ff;
            }
            .variant-option input[type="radio"] {
              margin-right: 10px;
            }
            .variant-modal-footer {
              margin-top: 20px;
              padding-top: 15px;
              border-top: 2px solid #eee;
              display: flex;
              gap: 10px;
              justify-content: flex-end;
            }
            .variant-modal-btn {
              padding: 10px 20px;
              border: none;
              border-radius: 5px;
              cursor: pointer;
              font-size: 16px;
              font-weight: 500;
            }
            .variant-modal-btn-cancel {
              background-color: #6c757d;
              color: white;
            }
            .variant-modal-btn-add {
              background-color: #007bff;
              color: white;
            }
            .variant-modal-btn:hover {
              opacity: 0.9;
            }
          `}
        </style>
      <div className="product__items--thumbnail">
        <Link 
          className="product__items--link" 
          to={`/product/${product.id}`}
          onMouseEnter={startImageCarousel}
          onMouseLeave={stopImageCarousel}
        >
          <img 
            className="product__items--img product__primary--img" 
            src={images[currentImage]} 
            alt="product-img" 
            style={{ width: '250px', height: '250px', objectFit: 'cover', display: 'block' }}
            onError={(e) => {
              e.target.src = '/assets/img/product/default.png';
            }}
          />
        </Link>
        {(discountPercent > 0) && (
          <div className="product__badge">
            <span className="product__badge--items sale">{discountPercent}% OFF</span>
          </div>
        )}
        <ul className="product__items--action">
          <li className="product__items--action__list">
            <button
              className={`product__items--action__btn${wishlist.isInWishlist(product.id) ? ' active' : ''}`}
              aria-label="Wishlist"
              onClick={() => {
                if (wishlist.isInWishlist(product.id)) {
                  wishlist.removeFromWishlist(product.id);
                } else {
                  wishlist.addToWishlist(product);
                }
              }}
              style={{ color: wishlist.isInWishlist(product.id) ? '#e63946' : undefined }}
            >
              <svg className="product__items--action__btn--svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path d="M352.92 80C288 80 256 144 256 144s-32-64-96.92-64c-52.76 0-94.54 44.14-95.08 96.81-1.1 109.33 86.73 187.08 183 252.42a16 16 0 0018 0c96.26-65.34 184.09-143.09 183-252.42-.54-52.67-42.32-96.81-95.08-96.81z" fill={wishlist.isInWishlist(product.id) ? '#e63946' : 'none'} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32"/>
              </svg>
              <span className="visually-hidden">Wishlist</span>
            </button>
          </li>
        </ul>
      </div>
      <div className="product__items--content text-center">
        <h3 className="product__items--content__title h4">
          <Link to={`/product/${product.id}`}>{product.name || 'Product'}</Link>
        </h3>
        {isInCart && cartItem ? (
          <div className="quantity__box product__quantity" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
            <button
              type="button"
              className="quantity__value decrease"
              aria-label="decrease quantity"
              onClick={() => updateQuantity(-1)}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: '600'
              }}
            >
              -
            </button>

            <div style={{ minWidth: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#333' }}>{cartItem.quantiy}</span>
            </div>

            <button
              type="button"
              className="quantity__value increase"
              aria-label="increase quantity"
              onClick={() => updateQuantity(1)}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: '600'
              }}
            >
              +
            </button>
          </div>
        ) : (
          <button 
            className="add__to--cart__btn" 
            onClick={async () => {
              if (hasVariations) {
                // Show variant selection modal
                setShowVariantModal(true);
              } else {
                // Add directly to cart
                try {
                  await cart.addToCart(product);
                } catch (error) {
                  console.error('Failed to add product to cart:', error);
                }
              }
            }}
          >
            + Add to cart
          </button>
        )}
        <div className="product__items--price">
          {hasDiscount ? (
            <>
              <span className="current__price">${sellPrice.toFixed(2)}</span>
              <span className="old__price">${originalPrice.toFixed(2)}</span>
            </>
          ) : (
            <span className="current__price">${originalPrice.toFixed(2)}</span>
          )}
        </div>
        <div className="product__items--rating d-flex align-items-center justify-content-center" style={{ marginTop: '8px', gap: '5px' }}>
          <ul className="d-flex" style={{ gap: '2px', margin: 0, padding: 0, listStyle: 'none' }}>
            {[...Array(5)].map((_, i) => (
              <li key={i} className="product__items--rating__list">
                <span className="product__items--rating__icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 10.105 9.732">
                    <path data-name="star - Copy" d="M9.837,3.5,6.73,3.039,5.338.179a.335.335,0,0,0-.571,0L3.375,3.039.268,3.5a.3.3,0,0,0-.178.514L2.347,6.242,1.813,9.4a.314.314,0,0,0,.464.316L5.052,8.232,7.827,9.712A.314.314,0,0,0,8.292,9.4L7.758,6.242l2.257-2.231A.3.3,0,0,0,9.837,3.5Z" transform="translate(0 -0.018)" fill={i < (product.rating || 0) ? "#FFA500" : "#e0e0e0"} />
                  </svg>
                </span>
              </li>
            ))}
          </ul>
          <span className="product__items--rating__count--number" style={{ fontSize: '13px', color: '#666' }}>
            ({product.total_rating || product.reviews || 0})
          </span>
        </div>
        {product.descriptions && (
          <div 
            className="product__items--description" 
            style={{ fontSize: '14px', color: '#666', marginTop: '8px', lineHeight: '1.4' }}
            dangerouslySetInnerHTML={{
              __html: product.descriptions.length > 80 
                ? product.descriptions.substring(0, 80) + '...' 
                : product.descriptions
            }}
          />
        )}
      </div>
    </div>

      {/* Variant Selection Popup Modal */}
      {showVariantModal && hasVariations && (
        <div 
          className="variant-modal-overlay"
          onClick={() => {
            setShowVariantModal(false);
          }}
        >
          <div 
            className="variant-modal-content"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="variant-modal-header">
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                Select {variations[0]?.name || 'Option'}
              </h3>
              <button 
                className="variant-modal-close"
                onClick={() => setShowVariantModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="variant-modal-body">
              <p style={{ marginBottom: '15px', color: '#666' }}>
                Please select a {variations[0]?.name?.toLowerCase() || 'variant'} for <strong>{product.name}</strong>
              </p>
              
              {variations[0]?.items?.map((item, index) => (
                <label 
                  key={index}
                  className={`variant-option ${selectedVariantIndex === index ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={`variant-${product.id}`}
                    checked={selectedVariantIndex === index}
                    onChange={() => {
                      setSelectedVariantIndex(index);
                    }}
                  />
                  <span style={{ fontSize: '16px', fontWeight: '500' }}>
                    {item.name || item.title}
                  </span>
                  {item.price && (
                    <span style={{ marginLeft: '10px', color: '#007bff', fontWeight: '600' }}>
                      ${parseFloat(item.price).toFixed(2)}
                    </span>
                  )}
                </label>
              ))}
            </div>

            <div className="variant-modal-footer">
              <button 
                className="variant-modal-btn variant-modal-btn-cancel"
                onClick={() => setShowVariantModal(false)}
              >
                Cancel
              </button>
              <button 
                className="variant-modal-btn variant-modal-btn-add"
                onClick={async () => {
                  try {
                    const productWithVariant = {
                      ...product,
                      selectedVariant: selectedVariantIndex,
                      variant: selectedVariantIndex
                    };
                    await cart.addToCart(productWithVariant);
                    setShowVariantModal(false);
                  } catch (error) {
                    console.error('Failed to add product to cart:', error);
                  }
                }}
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;
